import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import {
  ArrowRight,
  ArrowLeft,
  Calendar,
  Clock,
  Tag,
  BookOpen,
  Link2,
  Check,
  User,
  Eye,
  Sparkles,
  Share2,
} from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { t } from "../../i18n";
import api from "../../api/client";
import logger from "../../utils/logger";
import LoadingScreen from "../ui/Loading/Loading";
import Breadcrumb from "../ui/Breadcrumb/Breadcrumb";
import logo from "../../assets/logo/logo.png";
import "./BlogDetail.css";

/* ── Social SVG Icons ── */
const TwitterIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const FacebookIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const LinkedInIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const WhatsAppIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const TelegramIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
  </svg>
);

function useTranslation(lang) {
  return useCallback(
    (key, arFallback, enFallback) => {
      const translated = t(lang, key);
      if (translated && translated !== key) return translated;
      return lang === "ar" ? arFallback : enFallback;
    },
    [lang]
  );
}

function formatDate(dateString, lang) {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    if (lang === "ar") {
      return date.toLocaleDateString("ar-SA", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    }
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return dateString;
  }
}

function calculateReadingTime(content, lang) {
  if (!content) return lang === "ar" ? "3 دقائق قراءة" : "3 min read";
  const words = content.replace(/<[^>]*>/g, "").split(/\s+/).length;
  const minutes = Math.max(1, Math.ceil(words / 180));
  return lang === "ar" ? `${minutes} دقائق قراءة` : `${minutes} min read`;
}

function openSharePopup(url) {
  const width = 600;
  const height = 500;
  const left = window.innerWidth / 2 - width / 2;
  const top = window.innerHeight / 2 - height / 2;
  const features = `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`;
  const win = window.open(url, "_blank", features);
  if (win) win.focus();
}

function BlogActions({ post, lang, isRTL, tx }) {
  const [copied, setCopied] = useState(false);
  const url = typeof window !== "undefined" ? window.location.href : "";
  const text = post?.title || "";

  const shareLinks = [
    {
      key: "twitter",
      icon: <TwitterIcon size={16} />,
      label: "X (Twitter)",
      getHref: () =>
        `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
    },
    {
      key: "facebook",
      icon: <FacebookIcon size={16} />,
      label: "Facebook",
      getHref: () =>
        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    },
    {
      key: "linkedin",
      icon: <LinkedInIcon size={16} />,
      label: "LinkedIn",
      getHref: () =>
        `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    },
    {
      key: "whatsapp",
      icon: <WhatsAppIcon size={16} />,
      label: "WhatsApp",
      getHref: () =>
        `https://wa.me/?text=${encodeURIComponent(text + "\n" + url)}`,
    },
    {
      key: "telegram",
      icon: <TelegramIcon size={16} />,
      label: "Telegram",
      getHref: () =>
        `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
    },
  ];

  const handleShare = (e, getHref) => {
    e.preventDefault();
    const href = getHref();
    if (!href) return;
    openSharePopup(href);
  };

  const handleCopy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const input = document.createElement("input");
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <section className="blog-actions-section" aria-label={tx("article_actions", "إجراءات المقال", "Article actions")}>
      <div className="share-column">
        <span className="share-label">
          <Share2 size={15} />
          {tx("share_article", "شارك المقال مع أصدقائك", "Share this article")}
        </span>
        <div className="share-group">
          {shareLinks.map((link) => (
            <button
              key={link.key}
              className="share-btn"
              onClick={(e) => handleShare(e, link.getHref)}
              title={link.label}
              aria-label={link.label}
              type="button"
            >
              <span className="share-btn-inner">{link.icon}</span>
            </button>
          ))}
          <button
            className={`share-btn ${copied ? "copied" : ""}`}
            onClick={handleCopy}
            title={copied ? tx("copied", "تم النسخ", "Copied!") : tx("copy_link", "نسخ الرابط", "Copy link")}
            aria-label={copied ? tx("copied", "تم النسخ", "Copied!") : tx("copy_link", "نسخ الرابط", "Copy link")}
            type="button"
          >
            <span className="share-btn-inner">
              {copied ? <Check size={16} /> : <Link2 size={16} />}
            </span>
          </button>
        </div>
      </div>

      <Link to="/blog" className="readmore-cta-wrapper">
        <span className="readmore-cta">
          <BookOpen size={18} />
          <span>{tx("read_more_articles", "استكشف المزيد من المقالات", "Explore more articles")}</span>
          {isRTL ? <ArrowLeft size={16} /> : <ArrowRight size={16} />}
        </span>
      </Link>
    </section>
  );
}

export default function BlogDetail() {
  const { slug } = useParams();
  const { lang } = useLanguage();
  const isRTL = lang === "ar";
  const tx = useTranslation(lang);

  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (totalHeight > 0) {
        const current = window.scrollY;
        setScrollProgress(Math.min(100, Math.max(0, (current / totalHeight) * 100)));
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        setError("");
        const data = await api.apiGet(
          `blog/post?slug=${encodeURIComponent(slug)}&lang=${encodeURIComponent(lang)}`
        );
        if (data?.status === "success" && data.post) {
          setPost(data.post);
        } else {
          setError(data?.message || tx("load_error", "تعذّر تحميل المقال.", "Unable to load post."));
        }
      } catch (e) {
        logger.error("Error loading post", e);
        setError(tx("load_error", "حدث خطأ أثناء تحميل المقال.", "Error loading post."));
      } finally {
        setLoading(false);
      }
    }

    if (!slug) {
      setLoading(false);
      setError(tx("post_not_found", "المقال غير موجود.", "Post not found."));
      return;
    }

    load();
  }, [slug, lang, tx]);

  useEffect(() => {
    async function loadRelated() {
      try {
        const res = await api.apiGet(`blog?lang=${encodeURIComponent(lang)}`);
        if (res?.status === "success" && Array.isArray(res.posts)) {
          const filtered = res.posts.filter((p) => p.slug !== slug).slice(0, 3);
          setRelatedPosts(filtered);
        }
      } catch (e) {
        logger.error("Error loading related posts", e);
      }
    }
    loadRelated();
  }, [slug, lang]);

  if (loading) {
    return (
      <div className="blog-detail-page" dir={isRTL ? "rtl" : "ltr"}>
        <div className="blog-detail-container">
          <LoadingScreen lang={lang} />
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="blog-detail-page" dir={isRTL ? "rtl" : "ltr"}>
        <Breadcrumb
          items={[
            { label: tx("blog", "المدونة", "Blog"), link: "/blog" },
            { label: tx("error", "خطأ", "Error") }
          ]}
        />
        <div className="blog-detail-container">
          <div className="blog-detail-error">
            <div className="error-icon">
              <Tag size={40} strokeWidth={1.5} />
            </div>
            <p className="error-message">{error || tx("load_error", "تعذّر تحميل المقال.", "Unable to load post.")}</p>
            <Link to="/blog" className="error-back-link">
              {isRTL ? <ArrowRight size={18} /> : <ArrowLeft size={18} />}
              <span>{tx("back_to_blog", "العودة للمدونة", "Back to blog")}</span>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const imageSrc = post?.image_url ? api.buildUrl(post.image_url) : (post?.image ? api.buildUrl(post.image) : "");
  const hasImage = !!imageSrc;
  const showExcerpt = post.excerpt && post.excerpt !== post.title && post.excerpt.trim() !== "";
  const readingTimeStr = calculateReadingTime(post.content, lang);

  return (
    <div className="blog-detail-page" dir={isRTL ? "rtl" : "ltr"}>
      {/* Top Reading Progress Bar */}
      <div
        className="blog-reading-progress"
        style={{ width: `${scrollProgress}%` }}
        aria-hidden="true"
      />

      <Breadcrumb
        items={[
          { label: tx("blog", "المدونة", "Blog"), link: "/blog" },
          { label: post.title }
        ]}
      />

      <article className="blog-detail-container">
        {/* Header Hero Section */}
        <header className="blog-detail-hero">
          <div className="blog-hero-category-badge">
            <Sparkles size={14} />
            <span>{tx("editorial_article", "مقالات ومعرفة", "Editorial Post")}</span>
          </div>

          <h1 className="blog-hero-title">{post.title}</h1>
          <div className="blog-hero-title-line" />

          {showExcerpt && (
            <p className="blog-hero-excerpt">{post.excerpt}</p>
          )}

          {/* Author Card & Meta Bar */}
          <div className="blog-hero-author-card">
            <div className="author-avatar-box">
              <img src={logo} alt="Mousa Group" className="author-avatar-img" />
            </div>
            <div className="author-info">
              <div className="author-name">
                {isRTL ? "مجموعة موسى التقنية" : "Mousa Group Editorial"}
              </div>
              <div className="author-meta-details">
                {post.created_at && (
                  <span className="author-meta-item">
                    <Calendar size={13} />
                    {formatDate(post.created_at, lang)}
                  </span>
                )}
                <span className="meta-dot">•</span>
                <span className="author-meta-item">
                  <Clock size={13} />
                  {readingTimeStr}
                </span>
                {post.views > 0 && (
                  <>
                    <span className="meta-dot">•</span>
                    <span className="author-meta-item">
                      <Eye size={13} />
                      {post.views} {isRTL ? "مشاهدة" : "views"}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Hero Featured Cover Image */}
          {hasImage && (
            <div className="blog-hero-image-wrapper">
              <div className="blog-hero-image-inner">
                <img
                  src={imageSrc}
                  alt={post.title}
                  className="blog-hero-image"
                />
              </div>
            </div>
          )}
        </header>

        {/* Article Content Layout */}
        <div className="blog-detail-layout">
          <main className="blog-detail-main">
            <div
              className="blog-article-body"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content || "") }}
            />

            {/* Author Bio Footer Box */}
            <div className="blog-author-bio-footer">
              <div className="bio-avatar">
                <img src={logo} alt="Mousa Group" />
              </div>
              <div className="bio-content">
                <h3 className="bio-title">
                  {isRTL ? "مُحرر بواسطة فريق مجموعة موسى" : "Published by Mousa Group Team"}
                </h3>
                <p className="bio-desc">
                  {isRTL
                    ? "نقدم في مجموعة موسى مقالات ودراسات تقنية متخصصة تهدف لتطوير مهارات الشباب والشركات في التكنولوجيا والذكاء الاصطناعي."
                    : "At Mousa Group, we publish specialized technical insights and guides to empower developers and businesses in AI and software."}
                </p>
              </div>
            </div>

            {/* Social Share & Read More Actions */}
            <BlogActions post={post} lang={lang} isRTL={isRTL} tx={tx} />
          </main>
        </div>

        {/* Related Articles Section */}
        {relatedPosts.length > 0 && (
          <section className="blog-related-section">
            <div className="related-section-header">
              <h2 className="related-title">
                <Sparkles size={20} />
                <span>{isRTL ? "مقالات قد تهمك" : "Related Articles"}</span>
              </h2>
              <Link to="/blog" className="related-see-all">
                <span>{isRTL ? "عرض الكل" : "View All"}</span>
                {isRTL ? <ArrowLeft size={16} /> : <ArrowRight size={16} />}
              </Link>
            </div>

            <div className="related-grid">
              {relatedPosts.map((relPost) => {
                const relImg = relPost.image_url
                  ? api.buildUrl(relPost.image_url)
                  : (relPost.image ? api.buildUrl(relPost.image) : "");

                return (
                  <article key={relPost.id || relPost.slug} className="related-card">
                    <Link to={`/blog/${relPost.slug}`} className="related-card-img-link">
                      {relImg ? (
                        <img src={relImg} alt={relPost.title} className="related-card-img" />
                      ) : (
                        <div className="related-card-img-placeholder">
                          <BookOpen size={32} />
                        </div>
                      )}
                    </Link>
                    <div className="related-card-content">
                      <span className="related-card-date">
                        <Calendar size={12} />
                        {formatDate(relPost.created_at, lang)}
                      </span>
                      <h3 className="related-card-title">
                        <Link to={`/blog/${relPost.slug}`}>{relPost.title}</Link>
                      </h3>
                      {relPost.excerpt && (
                        <p className="related-card-excerpt">{relPost.excerpt}</p>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        )}
      </article>
    </div>
  );
}